import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";

export default function ContactSection() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ nombre: "", email: "", empresa: "", mensaje: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.email.trim() || !form.mensaje.trim()) {
      toast.error("Completa tu nombre, correo y qué te gustaría delegar.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      navigate("/gracias");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-[9px] border border-[#D3D1C7] bg-white px-3.5 py-2.5 text-[14px] text-brand-ink outline-none transition-colors duration-200 focus:border-brand-indigo focus:ring-2 focus:ring-brand-indigo/20";

  return (
    <section id="contacto" className="scroll-mt-24 py-20 sm:py-24" data-testid="contact-section">
      <div className="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[1fr_1.2fr]">
        <Reveal>
          <p className="text-[13px] uppercase tracking-[0.12em] text-brand-gray">Contacto</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight text-brand-ink">
            Hablemos de tu negocio
          </h2>
          <p className="mt-4 max-w-md text-[15px] leading-relaxed text-[#5F5E5A]">
            Cuéntanos qué tareas te gustaría delegar y te proponemos un plan a tu medida en
            menos de 24 horas.
          </p>
          <div className="mt-8 space-y-4">
            <a
              href="mailto:hola@asistentesvirtual.com"
              data-testid="contact-email"
              className="flex items-center gap-3 text-[15px] text-brand-ink hover:text-brand-indigo transition-colors"
            >
              <Mail size={20} className="text-brand-indigo" strokeWidth={1.8} />
              hola@asistentesvirtual.com
            </a>
            <a
              href="tel:+34633636438"
              data-testid="contact-phone"
              className="flex items-center gap-3 text-[15px] text-brand-ink hover:text-brand-indigo transition-colors"
            >
              <Phone size={20} className="text-brand-indigo" strokeWidth={1.8} />
              +34 633 636 438
            </a>
            <a
              href="https://wa.me/34633636438?text=Hola%2C%20me%20gustar%C3%ADa%20delegar%20algunas%20tareas."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-center gap-3 text-[15px] text-brand-ink hover:text-brand-mint transition-colors"
            >
              <MessageCircle size={20} className="text-brand-mint" strokeWidth={1.8} />
              WhatsApp: +34 633 636 438
            </a>
            <div className="flex items-center gap-3 text-[15px] text-brand-ink">
              <MessageCircle size={20} className="text-brand-indigo" strokeWidth={1.8} />
              WhatsApp de lunes a viernes, 9–18 h
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={onSubmit}
            noValidate
            data-testid="contact-form"
            className="rounded-[14px] border border-brand-line bg-brand-bgAlt p-7"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="f-nombre" className="mb-1.5 block text-[13px] text-[#5F5E5A]">Nombre</label>
                <input id="f-nombre" name="nombre" value={form.nombre} onChange={onChange} type="text" autoComplete="name" required className={inputCls} data-testid="input-nombre" />
              </div>
              <div>
                <label htmlFor="f-email" className="mb-1.5 block text-[13px] text-[#5F5E5A]">Correo</label>
                <input id="f-email" name="email" value={form.email} onChange={onChange} type="email" autoComplete="email" required className={inputCls} data-testid="input-email" />
              </div>
            </div>
            <div className="mt-4">
              <label htmlFor="f-empresa" className="mb-1.5 block text-[13px] text-[#5F5E5A]">Empresa o actividad</label>
              <input id="f-empresa" name="empresa" value={form.empresa} onChange={onChange} type="text" placeholder="Clínica, despacho, tienda online…" className={inputCls} data-testid="input-empresa" />
            </div>
            <div className="mt-4">
              <label htmlFor="f-mensaje" className="mb-1.5 block text-[13px] text-[#5F5E5A]">¿Qué te gustaría delegar?</label>
              <textarea id="f-mensaje" name="mensaje" value={form.mensaje} onChange={onChange} placeholder="Llamadas, facturas, viajes…" required rows={4} className={`${inputCls} resize-y`} data-testid="input-mensaje" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-5 w-full rounded-[10px] bg-brand-indigo py-3 text-[15px] font-medium text-white shadow-[0_8px_24px_rgba(79,70,229,0.3)] transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {loading ? "Enviando…" : "Solicitar propuesta"}
            </button>
            <p className="mt-3 text-center text-[12.5px] text-brand-gray">
              Te respondemos en menos de 24 horas laborables.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
